import React, { useState } from 'react';
import { 
  LayoutDashboard, 
  Package, 
  Building2, 
  Bus, 
  MessageSquare, 
  Settings, 
  LogOut,
  Shield,
  Download,
  Upload,
  RefreshCw,
  Clock,
  TrendingUp,
  Users,
  Star
} from 'lucide-react';
import { useCMS, useCMSAuth } from '../../store/cmsStore';
import type { CMSData } from '../../store/cmsStore';
import PackageEditor from './PackageEditor';
import HotelEditor from './HotelEditor';
import BusEditor from './BusEditor';
import TestimonialEditor from './TestimonialEditor';
import SettingsEditor from './SettingsEditor';

type AdminSection = 'dashboard' | 'packages' | 'hotels' | 'buses' | 'testimonials' | 'settings';

interface AdminDashboardProps {
  onLogout?: () => void;
}

export default function AdminDashboard({ onLogout }: AdminDashboardProps) {
  const { data, exportData, importData, resetData } = useCMS();
  const { logout } = useCMSAuth();
  const [activeSection, setActiveSection] = useState<AdminSection>('dashboard');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [importError, setImportError] = useState('');
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const handleExport = () => {
    const json = exportData();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sudra-cms-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    setImportError('');
    if (!importJson.trim()) {
      setImportError('يرجى إدخال أو رفع ملف JSON');
      return;
    }
    if (importData(importJson)) {
      setShowImportModal(false);
      setImportJson('');
      alert('تم الاستيراد بنجاح!');
    } else {
      setImportError('خطأ في البيانات. تأكد من صحة ملف JSON.');
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportError('');
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setImportJson(content);
    };
    reader.onerror = () => {
      setImportError('خطأ في قراءة الملف');
    };
    reader.readAsText(file);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const handleReset = () => {
    resetData();
    setShowResetConfirm(false);
    alert('تمت إعادة تعيين البيانات بنجاح!');
  };

  const handleLogout = () => {
    logout();
    onLogout?.();
  };

  const stats = [
    { 
      label: 'الباقات', 
      value: data.packages.filter(p => p.active).length, 
      total: data.packages.length,
      icon: Package, 
      color: 'bg-blue-500' 
    },
    { 
      label: 'الفنادق', 
      value: data.hotels.filter(h => h.active).length, 
      total: data.hotels.length,
      icon: Building2, 
      color: 'bg-green-500' 
    },
    { 
      label: 'الباصات', 
      value: data.buses.filter(b => b.active).length, 
      total: data.buses.length,
      icon: Bus, 
      color: 'bg-purple-500' 
    },
    { 
      label: 'التقييمات', 
      value: data.testimonials.filter(t => t.active).length, 
      total: data.testimonials.length,
      icon: Star, 
      color: 'bg-yellow-500' 
    },
  ];

  const menuItems = [
    { id: 'dashboard' as AdminSection, label: 'لوحة التحكم', icon: LayoutDashboard },
    { id: 'packages' as AdminSection, label: 'الباقات', icon: Package },
    { id: 'hotels' as AdminSection, label: 'الفنادق', icon: Building2 },
    { id: 'buses' as AdminSection, label: 'الباصات', icon: Bus },
    { id: 'testimonials' as AdminSection, label: 'التقييمات', icon: MessageSquare },
    { id: 'settings' as AdminSection, label: 'الإعدادات', icon: Settings },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'packages':
        return <PackageEditor />;
      case 'hotels':
        return <HotelEditor />;
      case 'buses':
        return <BusEditor />;
      case 'testimonials':
        return <TestimonialEditor />;
      case 'settings':
        return <SettingsEditor />;
      default:
        return (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500 font-cairo">{stat.label}</p>
                        <p className="text-2xl font-bold text-gray-900 font-cairo">
                          {stat.value}
                          <span className="text-sm text-gray-400 mr-1">/ {stat.total}</span>
                        </p>
                      </div>
                      <div className={`${stat.color} p-3 rounded-lg`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Last Updated */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="w-5 h-5 text-gray-400" />
                <h3 className="text-lg font-bold text-gray-900 font-cairo">معلومات النظام</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">آخر تحديث:</span>
                  <span className="mr-2 text-gray-900 font-cairo">
                    {new Date(data.lastUpdated).toLocaleString('ar-SA')}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">رقم الإصدار:</span>
                  <span className="mr-2 text-gray-900 font-cairo">v{data.version}</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <h3 className="text-lg font-bold text-gray-900 font-cairo mb-4">إجراءات سريعة</h3>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleExport}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition font-cairo"
                >
                  <Download className="w-4 h-4" />
                  تصدير البيانات
                </button>
                <button
                  onClick={() => setShowImportModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition font-cairo"
                >
                  <Upload className="w-4 h-4" />
                  استيراد البيانات
                </button>
                <button
                  onClick={() => setShowResetConfirm(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition font-cairo"
                >
                  <RefreshCw className="w-4 h-4" />
                  إعادة تعيين
                </button>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-brown-600" />
              <h1 className="text-xl font-bold text-gray-900 font-amiri">لوحة التحكم</h1>
            </div>
            <div className="flex items-center gap-4">
              <a
                href="/"
                className="text-sm text-gray-600 hover:text-brown-600 font-cairo transition"
              >
                العودة للموقع
              </a>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition font-cairo"
              >
                <LogOut className="w-4 h-4" />
                خروج
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <nav className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 sticky top-24">
              <ul className="space-y-2">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.id}>
                      <button
                        onClick={() => setActiveSection(item.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition font-cairo ${
                          activeSection === item.id
                            ? 'bg-brown-50 text-brown-700 font-bold'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        {item.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {renderContent()}
          </main>
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 font-amiri mb-4">استيراد البيانات</h3>
            
            {/* File Upload Area */}
            <label className="block mb-4 cursor-pointer">
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-brown-400 hover:bg-brown-50/30 transition">
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 font-cairo">اضغط هنا لرفع ملف JSON</p>
                <p className="text-xs text-gray-400 mt-1 font-cairo">أو اسحب وأفلت الملف هنا</p>
              </div>
              <input
                type="file"
                accept=".json,application/json"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>

            <div className="relative mb-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-2 text-gray-400 font-cairo">أو الصق النص يدوياً</span>
              </div>
            </div>

            <textarea
              value={importJson}
              onChange={(e) => { setImportJson(e.target.value); setImportError(''); }}
              className="w-full h-48 border border-gray-300 rounded-lg p-3 text-sm font-mono text-left"
              placeholder='{"packages":[], "hotels":[], ...}'
            />
            {importError && (
              <p className="text-red-500 text-sm mt-2 font-cairo">{importError}</p>
            )}
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportJson('');
                  setImportError('');
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-cairo"
              >
                إلغاء
              </button>
              <button
                onClick={handleImport}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-cairo"
              >
                استيراد
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirm Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 font-amiri mb-2">تأكيد إعادة التعيين</h3>
            <p className="text-gray-600 font-cairo mb-6">
              هل أنت متأكد من إعادة تعيين جميع البيانات؟ سيتم حذف جميع التغييرات والعودة للبيانات الافتراضية.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-cairo"
              >
                إلغاء
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-cairo"
              >
                إعادة تعيين
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}